<template>
  <teleport to="body">
    <div v-if="isVisible" class="confirm-overlay" @click="handleOverlayClick">
      <div class="confirm-dialog" @click.stop>
        <div class="confirm-header">
          <h3 class="confirm-title">{{ title }}</h3>
          <button class="confirm-close" @click="handleCancel">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        
        <div class="confirm-body">
          <div class="confirm-icon">
            <i class="fa-solid fa-question-circle"></i>
          </div>
          <p class="confirm-message">{{ message }}</p>
        </div>
        
        <div class="confirm-footer">
          <button 
            class="confirm-btn confirm-btn-cancel" 
            @click="handleCancel"
            :disabled="loading"
          >
            {{ cancelText }}
          </button>
          <button 
            class="confirm-btn confirm-btn-confirm" 
            @click="handleConfirm"
            :disabled="loading"
          >
            <i v-if="loading" class="fa-solid fa-spinner fa-spin"></i>
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  name: 'ConfirmDialog',
  props: {
    title: {
      type: String,
      default: 'Confirmação'
    },
    message: {
      type: String,
      required: true
    },
    confirmText: {
      type: String,
      default: 'Sim'
    },
    cancelText: {
      type: String,
      default: 'Não'
    },
    loading: {
      type: Boolean,
      default: false
    },
    closeOnOverlay: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isVisible: false,
      resolvePromise: null,
      rejectPromise: null
    }
  },
  methods: {
    show() {
      this.isVisible = true;
      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
        this.rejectPromise = reject;
      });
    },
    
    hide() {
      this.isVisible = false;
      this.resolvePromise = null;
      this.rejectPromise = null;
    },
    
    handleConfirm() {
      if (this.resolvePromise) {
        this.resolvePromise(true);
      }
      this.hide();
    },
    
    handleCancel() {
      if (this.rejectPromise) {
        this.rejectPromise(false);
      }
      this.hide();
    },
    
    handleOverlayClick() {
      if (this.closeOnOverlay) {
        this.handleCancel();
      }
    },
    
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.isVisible) {
        this.handleCancel();
      }
    }
  },
  
  mounted() {
    // Adicionar listener para a tecla ESC
    document.addEventListener('keydown', this.handleKeyDown);
  },
  
  beforeUnmount() {
    // Remover listener
    document.removeEventListener('keydown', this.handleKeyDown);
  }
}
</script>

<style scoped>
.confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(3px);
}

.confirm-dialog {
  background: var(--bg-primary, #ffffff);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  max-width: 400px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  animation: confirmDialogEnter 0.3s ease-out;
}

@keyframes confirmDialogEnter {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.confirm-header {
  padding: 20px 20px 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.confirm-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary, #333333);
}

.confirm-close {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: var(--text-secondary, #666666);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.confirm-close:hover {
  background-color: var(--bg-secondary, #f5f5f5);
  color: var(--text-primary, #333333);
}

.confirm-body {
  padding: 20px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.confirm-icon {
  font-size: 2rem;
  color: var(--warning-color, #f59e0b);
  flex-shrink: 0;
}

.confirm-message {
  margin: 0;
  font-size: 1rem;
  line-height: 1.5;
  color: var(--text-primary, #333333);
}

.confirm-footer {
  padding: 0 20px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.confirm-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 80px;
  justify-content: center;
}

.confirm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.confirm-btn-cancel {
  background-color: var(--bg-secondary, #f5f5f5);
  color: var(--text-primary, #333333);
  border: 1px solid var(--border-color, #e0e0e0);
}

.confirm-btn-cancel:hover:not(:disabled) {
  background-color: var(--bg-tertiary, #eeeeee);
}

.confirm-btn-confirm {
  background-color: var(--danger-color, #dc3545);
  color: white;
}

.confirm-btn-confirm:hover:not(:disabled) {
  background-color: var(--danger-hover, #c82333);
}

/* Tema escuro */
@media (prefers-color-scheme: dark) {
  .confirm-dialog {
    background: var(--bg-primary, #1a1a1a);
  }
  
  .confirm-title {
    color: var(--text-primary, #ffffff);
  }
  
  .confirm-close {
    color: var(--text-secondary, #cccccc);
  }
  
  .confirm-close:hover {
    background-color: var(--bg-secondary, #2a2a2a);
    color: var(--text-primary, #ffffff);
  }
  
  .confirm-message {
    color: var(--text-primary, #ffffff);
  }
  
  .confirm-btn-cancel {
    background-color: var(--bg-secondary, #2a2a2a);
    color: var(--text-primary, #ffffff);
    border-color: var(--border-color, #404040);
  }
  
  .confirm-btn-cancel:hover:not(:disabled) {
    background-color: var(--bg-tertiary, #3a3a3a);
  }
}
</style> 